import { Fragment, useMemo, type FC } from 'react';

import { ThrowError } from '#apps/client-web/app/components/throw-error';
import { routes } from '#apps/client-web/app/routes';
import { DirectionFacingLabel } from '#apps/client-web/features/audit-form-windows/constants';

import {
  type AuditWindowsFormEntry,
  type DirectionFacing,
} from '#packages/data-collection/models/audit-windows-form';
import { type ViewModelProps } from '#packages/web/types/view-model-props';
import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import { EmptyPage, LoadingPage } from '#packages/web/layouts/page/templates';
import { Fab } from '#packages/web/foundations/buttons/fab';

import { type AuditFormWindowsPageViewModel } from './audit-form-windows-page.view-model';
import { AuditFormWindowsPageRenderer } from './audit-form-windows-page.renderer';

export type AuditFormWindowsPageContainerProps =
  ViewModelProps<AuditFormWindowsPageViewModel> & LoggingContext;

export const AuditFormWindowsPageContainer: FC<
  AuditFormWindowsPageContainerProps
> = ({ viewModel }) => {
  const { auditId, auditFormQuery, thumbnailFileIds, thumbnailMap, onCreate } =
    viewModel;

  const groups = useMemo(() => {
    const entries = auditFormQuery.data?.entries ?? [];

    // Group by directionFacing
    const grouped: Record<
      string,
      { entry: AuditWindowsFormEntry; globalIndex: number }[]
    > = {};
    entries.forEach((entry, globalIndex) => {
      const key = entry.directionFacing ?? 'unspecified';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push({ entry, globalIndex });
    });

    return Object.entries(grouped).map(([directionFacing, groupEntries]) => ({
      id: directionFacing,
      label:
        directionFacing === 'unspecified'
          ? 'Unspecified'
          : (DirectionFacingLabel[directionFacing as DirectionFacing] ??
            directionFacing),
      entries: groupEntries.map(({ entry, globalIndex }) => ({
        link: routes.audit.getEnergyAuditWindowsFormEntryPath(
          auditId!,
          globalIndex,
        ),
        entry,
        thumbnail: (() => {
          const fileId = thumbnailFileIds[globalIndex];
          return fileId ? (thumbnailMap[fileId] ?? null) : null;
        })(),
      })),
    }));
  }, [auditFormQuery.data, auditId, thumbnailFileIds, thumbnailMap]);

  const isLoading = auditFormQuery.isLoading;
  const isEmpty = auditFormQuery.isSuccess && groups.length < 1;
  const hasData = !isLoading && !isEmpty;

  return (
    <Fragment>
      {isLoading && <LoadingPage />}
      {isEmpty && <EmptyPage onActionButtonClick={onCreate} />}
      {hasData && <AuditFormWindowsPageRenderer groups={groups} />}
      {hasData && <Fab onClick={onCreate} />}
      {auditFormQuery.isError && <ThrowError error={auditFormQuery.error} />}
    </Fragment>
  );
};
